import styled from 'styled-components';
import { LocationsOptions } from '../pages/activate';
import { Button } from '../../../components/Button';
import ukFlag from '../../../assets/images/flags/uk-flag.png';
import ieFlag from '../../../assets/images/flags/ie-flag.png';

type SelectLocationProps = {
  location: LocationsOptions;
  setLocation: (location: LocationsOptions) => void;
  onGoBack?: () => void;
  onClick: () => void;
};

export const SelectLocation = ({
  location,
  setLocation,
  onGoBack,
  onClick
}: SelectLocationProps) => {
  return (
    <Container>
      <Title>Select Region</Title>

      <RadioContainer>
        <RadioWrapper 
          data-selected={location === 'uk'} 
          onClick={() => setLocation('uk')}
        >
          <Radio
            type="radio"
            value="uk"
            checked={location === 'uk'}
            onChange={() => setLocation('uk')}
            id="radio-uk"
          />
          <Label htmlFor="radio-uk">
            <FlagWrapper>
              <FlagImg src={ukFlag} />
            </FlagWrapper>
            United Kingdom
          </Label>
        </RadioWrapper>
        
        <RadioWrapper 
          data-selected={location === 'ie'} 
          onClick={() => setLocation('ie')}
        >
          <Radio
            type="radio"
            value="ie"
            checked={location === 'ie'}
            onChange={() => setLocation('ie')}
            id="radio-ie"
          />
          <Label htmlFor="radio-ie">
            <FlagWrapper>
              <FlagImg src={ieFlag} />
            </FlagWrapper>
            Ireland
          </Label>
        </RadioWrapper>
      </RadioContainer>
      
      <InfoWrapper>
        <p>Select the region your camera will be located.</p>
        <p>Note: the camera will only work in the selected region.</p>
      </InfoWrapper>
      
      <ButtonWrapper>
        {onGoBack && <Button onClick={onGoBack}>Back</Button>}
        <Button onClick={onClick}>Next</Button>
      </ButtonWrapper>
    </Container>
  );
};

const RadioContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
  width: 100%;
  max-width: 350px;
`;

const RadioWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray};
  }
  
  /* Add a selected state style */
  &[data-selected="true"] {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.primary + '10'};
  }
`;

const Radio = styled.input`
  /* Hide the default radio button */
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSize.medium};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  position: relative;
  padding-left: 35px;
  user-select: none;
  width: 100%;
  
  /* Create custom radio button */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 22px;
    width: 22px;
    background-color: #fff;
    border: 2px solid ${({ theme }) => theme.colors.gray500};
    border-radius: 50%;
    
    @media (max-width: 850px) {
      height: 24px;
      width: 24px;
    }
  }
  
  /* Add the dot when checked */
  &::after {
    content: '';
    position: absolute;
    left: 7px;
    top: 50%;
    transform: translateY(-50%);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    display: none;
    
    @media (max-width: 850px) {
      left: 8px;
      width: 12px;
      height: 12px;
    }
  }
`;

const FlagWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FlagImg = styled.img`
  width: 1.8rem;
  height: auto;
  border-radius: 0.2rem;
`;

const InfoWrapper = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.primary200};
  border-radius: 0.5rem;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: center;
  max-width: 350px;
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const Title = styled.h3`
  margin: 0;
  text-align: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  width: 100%;
`;

// Add this to the component style to make the dot appear
const style = document.createElement('style');
style.innerHTML = `
  input[type="radio"]:checked + label::after {
    display: block;
  }
`;
document.head.appendChild(style);
